// Version: 1.7

type TextTranslator = (source:string) => string;
let textTranslator:TextTranslator = (source) => source;

export function setTextTranslator(translator?:TextTranslator) {
	textTranslator = translator ?? ((source) => source);
}

const TRANSLATABLE_ATTRIBUTES = new Set(['aria-label', 'label', 'placeholder', 'title']);

export const render = function(src:RenderNode):Node|Text {
	if (!src) return document.createTextNode("");
	if (typeof src == 'string') {
		return document.createTextNode(textTranslator(src));
	} 
	if (typeof src == 'object') {
		let tag = src.tag;
		if (!tag) tag = 'div';
		let node = document.createElement(tag);
		// if ('className' in src) node.className = src.className!;
		if (src.hasOwnProperty('className')) node.className = src.className!;
		if ('style' in src) {
			for (let k in src.style) {
				node.style[k as any] = src.style[k];
			}
		}
		if ('dataset' in src) {
			for (const k in src.dataset)
				node.dataset[k] = src.dataset[k];
		}
		if ('selected' in src && src.selected) { node.setAttribute('selected', 'selected'); }
		if ('disabled' in src) { (node as HTMLInputElement).disabled = src.disabled!; }
		if ('checked' in src) { (node as HTMLInputElement).checked = src.checked!; }

		for (const k in src) {
			const specialKeys = ['tag', 'children', 'asyncChildren', 'className', 'style', 'dataset', 'disabled', 'selected', 'checked', 'hook'];
			if (!specialKeys.includes(k)) {
				if (typeof src[k as keyof RenderNode] == 'function') 
					(node[k as keyof HTMLElement] as any) = src[k as keyof RenderNode];
				else {
					const value = src[k as keyof RenderNode] as string;
					node.setAttribute(k, TRANSLATABLE_ATTRIBUTES.has(k) && typeof value === 'string' ? textTranslator(value) : value);
				}
			}
		}
		if ('children' in src) {
			const iterateOnChildren = (children:RenderNode[]) => {
				const arrayChildren =  children instanceof Array ? children! : [children!];
				for (const child of arrayChildren) {
					asyncFlatten(child, (n:any) => {
						const r = render(n);
						node.appendChild(r);
					})
				}	
			}
			
			asyncFlatten(src.children, iterateOnChildren)
		}
		if (('hook' in src)) src.hook!(node);
		return node;
	} else if (typeof src == 'function') {
		// does not handle promises
		return render((src as any)())
	}
	return document.createTextNode(src);
}

function asyncFlatten(item:any, effect:(e:any)=>void) {
	if (typeof item === 'function') {
		const r = (item as any)();
		if (r instanceof Promise) {
			r.then(effect)
		} else {
			effect(r)
		}
	} else {
		effect(item);
	}
}

export const renderHTML = function(src:RenderNode):string {
	if (!src) return "";
	if (typeof src == 'string') {
		return textTranslator(src);
	} 
	if (typeof src == 'object') {
		let tag = src.tag;
		if (!tag) tag = 'div';
		let node = `<${tag}`;
		if (src.hasOwnProperty('className')) node += ` class='${src.className!}'`; //node.className = src.className!
		if ('style' in src) {
			let style = '';
			const camelToCssCase = (str:string) => str.replace(/[A-Z]/g, (letter:string) => `-${letter.toLowerCase()}`);
			for (let k in src.style) {
				style += ` ${camelToCssCase(k)}:${src.style[k]};`;
			}
			node += ` style="${style}"`;
		}
		if ('dataset' in src) {
			for (const k in src.dataset) {
				node += ` data-${k}="${src.dataset[k]}"`;
			}
		}
		if ('disabled' in src) { node += src.disabled ? ` disabled` : ``; }
		if ('selected' in src) { node += src.selected ? ` selected` : ``; }
		if ('checked' in src) { node += src.checked ? ` checked` : ``; }

		for (const k in src) {
			const specialKeys = ['tag', 'children', 'className', 'style', 'dataset', 'disabled', 'selected', 'checked', 'hook'];
			if (!specialKeys.includes(k)) {
				if (typeof src[k as keyof RenderNode] == 'function') {
					node += ` ${k}="${(src[k as keyof RenderNode] as any).toString()}"`
				} else {
					node += ` ${k}="${src[k as keyof RenderNode]}"`
				}
			}
		}
		node += `>`;
		if ('children' in src) {
			const children = src.children instanceof Array ? src.children! : [src.children!];
			for (const child of children) {
				let c = renderHTML(child);
				if (c) {
					node += `${c}`;
				}
			}
		}
		node += `</${tag}>`;
		// if (('hook' in src)) src.hook!(node);
		return node;
	} else if (typeof src == 'function') {
		// does not handle promises
		return renderHTML((src as any)())
	}
	return src;
}

export type RenderNode = {
	[attr: string]: unknown;
	lang?:string;
	charset?:string;
	name?:string;
	content?:string;
	href?:string;
	target?:string;
	rel?:string;
	children?:RenderNode|RenderNode[];
	tag?:string;
	id?:string;
	className?:string;
	style?:any;
	dataset?:any;
	src?:string|URL;
	title?:string;
	slot?:string;
	for?:string;
	appearance?:string;
	selected?:boolean;
	checked?:boolean;
	placeholder?:string;
	autocomplete?:string;
	required?:boolean;
	multiple?:boolean;
	draggable?:boolean;
	value?:any;
	min?:number;
	max?:number;
	label?:string;
	list?:string;
	disabled?:boolean;
	contentEditable?:boolean;
	type?:string;
	rows?: number;
	autoplay?:boolean;
	playsinline?:boolean;
	width?: number;
	height?: number;
	hook?:((e:HTMLElement)=>void)|((e:HTMLElement)=>Promise<void>);
	onclick?: ((e:Event)=>void)|((e:Event)=>Promise<void>);
	onchange?: ((e:Event)=>void)|((e:Event)=>Promise<void>);
	onkeyup?: ((e:KeyboardEvent)=>void)|((e:KeyboardEvent)=>Promise<void>);
	onkeydown?: ((e:KeyboardEvent)=>void)|((e:KeyboardEvent)=>Promise<void>);
	oninput?: ((e:Event)=>void)|((e:Event)=>Promise<void>);
	onfocus?: ((e:Event)=>void)|((e:Event)=>Promise<void>);
	onblur?: ((e:Event)=>void)|((e:Event)=>Promise<void>);
	ondragstart?: ((e:Event)=>void)|((e:Event)=>Promise<void>);
	ondragend?: ((e:Event)=>void)|((e:Event)=>Promise<void>);
	ondragenter?: ((e:Event)=>void)|((e:Event)=>Promise<void>);
	ondragleave?: ((e:Event)=>void)|((e:Event)=>Promise<void>);
	ondragover?: ((e:Event)=>void)|((e:Event)=>Promise<void>);
	ondrop?: ((e:Event)=>void)|((e:Event)=>Promise<void>);
}|string|(()=>{})|null;
